import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Row, Col, Alert, Table, Modal } from 'react-bootstrap';
import axios from 'axios';

const ProductVideoUpload = () => {
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [videos, setVideos] = useState([]);
  const [alertMessage, setAlertMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [existingVideos, setExistingVideos] = useState([]);
  const [videoPath, setVideoPath] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [previewVideos, setPreviewVideos] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedVideoId, setSelectedVideoId] = useState(null); // For editing a video

  useEffect(() => {
    // Fetch products and existing videos on component mount
    fetchProducts();
    fetchExistingVideos();
  }, []);

  // Fetch products for the dropdown
  const fetchProducts = () => {
    axios.get('https://api.biznetusa.com/api/get-products')
      .then(response => {
        setProducts(response.data.products);
      })
      .catch(() => {
        setErrorMessage('Error fetching products.');
      });
  };

  // Fetch existing videos
  const fetchExistingVideos = () => {
    axios.get('https://api.biznetusa.com/api/get-productvideos')
      .then(response => {
        setExistingVideos(response.data.products);
        setVideoPath(response.data.imagePath);
      })
      .catch(() => {
        setErrorMessage('Error fetching product videos.');
      });
  };

  // Handle file input change and show video previews
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setVideos(files);
    const videoPreviews = files.map(file => URL.createObjectURL(file));
    setPreviewVideos(videoPreviews);
    setShowModal(true); // Show modal with preview
  };

  // Handle product selection change
  const handleProductChange = (e) => {
    setSelectedProductId(e.target.value);
  };

  // Handle form submission for uploading videos
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedProductId || videos.length === 0) {
      setErrorMessage('Please select a product and upload at least one video.');
      return;
    }

    const formData = new FormData();
    formData.append('p_id', selectedProductId);
    videos.forEach(video => {
      formData.append('video[]', video);
    });

    axios.post('https://api.biznetusa.com/api/store-productvideo', formData)
      .then(() => {
        setAlertMessage('Videos uploaded successfully!');
        setErrorMessage('');
        setVideos([]);
        setSelectedProductId('');
        fetchExistingVideos();
        setShowModal(false); // Close the modal after upload
      })
      .catch(() => {
        setErrorMessage('Error uploading videos.');
      });
  };

  // Handle delete video
  const handleDeleteVideo = (id) => {
    axios.delete(`https://api.biznetusa.com/api/deleteproductvideos/${id}`)
      .then(() => {
        setAlertMessage('Video deleted successfully!');
        fetchExistingVideos(); // Refresh the list after deletion
      })
      .catch(() => {
        setErrorMessage('Error deleting video.');
      });
  };

  // Handle edit video
  const handleEditVideo = () => {
    const formData = new FormData();
    if (videos.length > 0) {
      formData.append('video', videos[0]); // Only editing one video
      axios.post(`https://api.biznetusa.com/api/update-productvideo/${selectedVideoId}`, formData)
        .then(() => {
          setAlertMessage('Video edited successfully!');
          setErrorMessage('');
          fetchExistingVideos();
          setShowModal(false); // Close the modal after edit
        })
        .catch(() => {
          setErrorMessage('Error editing video.');
        });
    } else {
      setErrorMessage('Please select a video to edit.');
    }
  };

  // Open modal to edit video
  const handleOpenEditModal = (id) => {
    setSelectedVideoId(id);
    setIsEditing(true);
    setShowModal(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setShowModal(false);
    setPreviewVideos([]);
    setIsEditing(false);
  };

  return (
    <Container>
      <Row className="my-4">
        <Col>
          <h2>{isEditing ? 'Edit Product Video' : 'Upload Product Videos'}</h2>
          {alertMessage && <Alert variant="success">{alertMessage}</Alert>}
          {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
          <Form onSubmit={isEditing ? handleEditVideo : handleSubmit}>
            <Form.Group controlId="formProductSelect">
              <Form.Label>Select Product</Form.Label>
              <Form.Control as="select" value={selectedProductId} onChange={handleProductChange} required>
                <option value="">Select a product</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.title}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group controlId="formFileUpload">
              <Form.Label>{isEditing ? 'Select a New Video to Edit' : 'Select Videos'}</Form.Label>
              <Form.Control type="file" multiple={!isEditing} accept="video/*" onChange={handleFileChange} required />
              <Form.Text className="text-muted">
                {isEditing ? 'Choose a new video for editing.' : 'You can select multiple video files.'}
              </Form.Text>
            </Form.Group>

            <Button variant="primary" type="submit">
              {isEditing ? 'Save Changes' : 'Upload Videos'}
            </Button>
          </Form>
        </Col>
      </Row>

      <Row className="my-4">
        <Col>
          <h3>Existing Product Videos</h3>
          {existingVideos.length > 0 ? (
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Product ID</th>
                  <th>Video</th>
                  <th>Uploaded On</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {existingVideos.map((video) => (
                  <tr key={video.id}>
                    <td>{video.p_id}</td>
                    <td>
                      <video width="320" height="240" controls>
                        <source src={`${videoPath}${video.video}`} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    </td>
                    <td>{new Date(video.created_at).toLocaleString()}</td>
                    <td>
                      <Button variant="danger" onClick={() => handleDeleteVideo(video.id)}>Delete</Button>{' '}
                      <Button variant="warning" onClick={() => handleOpenEditModal(video.id)}>Edit</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <Alert variant="info">No videos found.</Alert>
          )}
        </Col>
      </Row>

      {/* Modal for previewing and editing videos */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? 'Edit Selected Video' : 'Preview Selected Videos'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewVideos.length > 0 ? (
            previewVideos.map((videoSrc, index) => (
              <div key={index}>
                <video width="320" height="240" controls>
                  <source src={videoSrc} type="video/mp4" />
                </video>
              </div>
            ))
          ) : (
            <Alert variant="info">No videos selected.</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
          {isEditing && (
            <Button variant="primary" onClick={handleEditVideo}>
              Save Changes
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ProductVideoUpload;
